import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ChevronDown, Truck, Users, ShoppingCart, Building,
  Activity, DollarSign, Briefcase, Factory, CheckCircle2, Car,
} from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';


const industries = [
  {
    id: 'logistics',
    icon: Truck,
    color: 'from-blue-500 to-cyan-500',
    glow: 'rgba(59,130,246,0.2)',
    title: 'Logistik & Transport',
    summary: 'Automatisera lagerflöden, fraktbokning och leveransspårning.',
    examples: [
      'Automatisk orderhantering och lagerstyrning',
      'AI-baserad ruttoptimering för leveranser',
      'Realtidsnotifieringar till kunder vid statusändring',
      'Integration mot WMS, ERP och fraktbolag-API:er',
      'Automatiserade tulldeklarationer och dokumentation',
    ],
    savings: '14 timmar/vecka',
  },
  {
    id: 'hr',
    icon: Users,
    color: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.2)',
    title: 'HR & Rekrytering',
    summary: 'Från CV-screening till onboarding — helt automatiserat.',
    examples: [
      'AI-driven CV-screening och kandidatranking',
      'Automatisk schemaläggning av intervjuer',
      'Digital onboarding med formulär och godkännanden',
      'Löpande påminnelser och kommunikation med kandidater',
      'Integration mot HR-system (Teamtailor, Workday)',
    ],
    savings: '20 timmar/vecka',
  },
  {
    id: 'ecommerce',
    icon: ShoppingCart,
    color: 'from-pink-500 to-rose-500',
    glow: 'rgba(236,72,153,0.2)',
    title: 'E-handel & Retail',
    summary: 'Kundtjänst, orderflöde och lagerstatus — dygnet runt.',
    examples: [
      'AI-chatbot för kundtjänst och produktfrågor',
      'Automatisk orderbekräftelse och returhantering',
      'Produktkataloguppdatering från leverantörs-feeds',
      'Prisoptimering baserat på lager och konkurrenter',
      'Automatiserad lojalitetsprogram och kampanjutskick',
    ],
    savings: '25 timmar/vecka',
  },
  {
    id: 'property',
    icon: Building,
    color: 'from-amber-500 to-orange-500',
    glow: 'rgba(245,158,11,0.2)',
    title: 'Fastighet & Bygg',
    summary: 'Felanmälningar, hyresavtal och projektrapporter automatiserat.',
    examples: [
      'AI-chatbot för hyresgästfrågor och felanmälan',
      'Automatisk bokningsbekräftelse för visningar',
      'Generering av hyresavtal och dokumentation',
      'Eskalering av akuta ärenden till rätt person',
      'Periodicitet-påminnelser för besiktningar och service',
    ],
    savings: '18 timmar/vecka',
  },
  {
    id: 'health',
    icon: Activity,
    color: 'from-teal-500 to-emerald-500',
    glow: 'rgba(20,184,166,0.2)',
    title: 'Hälsa & Life Science',
    summary: 'Säkra patientflöden och GDPR-kompatibel dokumentation.',
    examples: [
      'Automatiserad patientbokning och påminnelser',
      'AI-formulär för symtominsamling och triagering',
      'GDPR-kompatibel lagring och hantering av journaler',
      'Automatiserad rapportgenerering för myndigheter',
      'Integration mot journalsystem (TakeCare, Cambio)',
    ],
    savings: '22 timmar/vecka',
  },
  {
    id: 'finance',
    icon: DollarSign,
    color: 'from-emerald-500 to-green-500',
    glow: 'rgba(16,185,129,0.2)',
    title: 'Fintech & Finans',
    summary: 'Fakturor, rapporter och compliance utan manuell handpåläggning.',
    examples: [
      'Automatisk fakturabehandling och kontering',
      'AI-baserad kreditbedömning och riskanalys',
      'Periodiserade finansrapporter och bokslut',
      'Compliance-rapportering (AML, KYC)',
      'Integration mot Fortnox, Visma och Bankgirot',
    ],
    savings: '30 timmar/vecka',
  },
  {
    id: 'management',
    icon: Briefcase,
    color: 'from-indigo-500 to-violet-500',
    glow: 'rgba(99,102,241,0.2)',
    title: 'Management & Konsult',
    summary: 'Research, rapportering och intern kommunikation på autopilot.',
    examples: [
      'AI-agenter för research och omvärldsbevakning',
      'Automatisk mötesanteckning och uppföljning',
      'Generering av kunddokument och presentations',
      'Intern kunskapsbas med AI-sökning',
      'Automatiserade status-updates till kunder',
    ],
    savings: '12 timmar/vecka',
  },
  {
    id: 'automotive',
    icon: Car,
    color: 'from-slate-500 to-gray-600',
    glow: 'rgba(100,116,139,0.2)',
    title: 'Fordon & Däckbranschen',
    summary: 'Automatisera säsongsbokning, schemaläggning och kundkommunikation.',
    examples: [
      'AI-bokningsagent för däckbyte — svarar dygnet runt',
      'Intelligent schemaläggning baserat på lager och kapacitet',
      'Automatiska bokningsbekräftelser och SMS-påminnelser',
      'Integration mot befintligt kassa- och affärssystem',
      'Eskalering till personal vid undantag eller komplexa ärenden',
    ],
    savings: '28 timmar/vecka',
  },
  {
    id: 'manufacturing',
    icon: Factory,
    color: 'from-cyan-500 to-blue-500',
    glow: 'rgba(6,182,212,0.2)',
    title: 'Tillverkning & Industri',
    summary: 'Kvalitetskontroll, underhållsschema och inköpsorder — automatiserat.',
    examples: [
      'Prediktivt underhåll baserat på sensordata',
      'Automatiska inköpsorder vid lågt lagersaldo',
      'AI-kvalitetskontroll med bildanalys',
      'Digital produktionsdagbok och avvikelsehantering',
      'Integration mot SCADA och MES-system',
    ],
    savings: '35 timmar/vecka',
  },
];

const AccordionCard = ({
  item, isOpen, onToggle,
}: {
  item: typeof industries[0];
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-20px' }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
        isOpen ? 'border-indigo-200 shadow-lg' : 'border-border-light shadow-sm hover:border-indigo-100 hover:shadow-md'
      }`}
      onMouseEnter={e => { if (!isOpen) (e.currentTarget as HTMLElement).style.boxShadow = `0 10px 30px ${item.glow}`; }}
      onMouseLeave={e => { if (!isOpen) (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
    >
      <button
        className="w-full flex items-center gap-4 p-6 text-left"
        onClick={onToggle}
      >
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shrink-0 shadow-md`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-bold text-text-primary mb-0.5">{item.title}</h3>
          <p className="text-[13px] text-text-secondary truncate">{item.summary}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="hidden sm:block text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
            ≈ {item.savings}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-text-secondary transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="px-6 pb-6 border-t border-border-light pt-5">
              <p className="text-sm font-semibold text-text-secondary mb-3">Vad vi automatiserar åt er:</p>
              <ul className="space-y-2.5">
                {item.examples.map(ex => (
                  <li key={ex} className="flex items-start gap-2.5 text-[14px] text-text-secondary">
                    <CheckCircle2 className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                    {ex}
                  </li>
                ))}
              </ul>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex items-start gap-2 bg-red-50 rounded-xl border border-red-100 px-4 py-3">
                  <span className="text-xs font-bold text-red-500 uppercase tracking-wide shrink-0 mt-0.5">Före</span>
                  <span className="text-xs text-text-secondary leading-relaxed">Manuell hantering — tid som försvinner varje vecka</span>
                </div>
                <div className="flex items-start gap-2 bg-emerald-50 rounded-xl border border-emerald-100 px-4 py-3">
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide shrink-0 mt-0.5">Efter</span>
                  <span className="text-xs text-text-secondary leading-relaxed">Automatiserat flöde — ingenting faller mellan stolarna</span>
                </div>
              </div>
              <div className="mt-3 pt-4 border-t border-border-light flex items-center justify-between">
                <span className="text-sm text-text-secondary">
                  Tid sparad: <span className="font-bold text-text-primary">{item.savings}</span>
                  <span className="text-emerald-600 font-semibold ml-2">≈ 10 000–50 000 kr/mån</span>
                </span>
                <Link to="/contact">
                  <button className="text-sm font-semibold text-brand-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors">
                    Skicka förfrågan <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const AutomationsPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [openId, setOpenId] = useState<string | null>('logistics');

  const toggle = (id: string) => setOpenId(prev => prev === id ? null : id);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <Helmet>
        <title>Vad kan ditt företag automatisera? — Aifinity AB</title>
        <meta name="description" content="Se exakt vilka processer vi kan automatisera i din bransch — och hur många timmar ni sparar per vecka. Logistik, HR, e-handel, fastighet och mer." />
        <meta property="og:url" content="https://aifinity.se/automations" />
        <link rel="canonical" href="https://aifinity.se/automations" />
      </Helmet>
      <CorporateNavbar />

      <LightPageHero
        badge="AI-AUTOMATION"
        heading={<>Detta kan du sluta göra<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>manuellt redan i år</span></>}
        subtext="Klicka på din bransch och se exakt vad vi tar bort från att-göra-listan."
      />

      {/* ── Exempel på vad vi faktiskt bygger ── */}
      <section className="py-14 bg-surface-subtle border-b border-border-light">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-6 text-center"
            >
              Exempel på vad vi faktiskt bygger
            </motion.p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { from: 'Mail',      to: 'automatiska svar och ärendestatus',   arrow: '→' },
                { from: 'Bokningar', to: 'direkt in i system utan manuell hantering', arrow: '→' },
                { from: 'Excel',     to: 'automatiska dashboards och rapporter',  arrow: '→' },
                { from: 'Support',   to: 'chatbot som svarar 24/7',              arrow: '→' },
              ].map(({ from, to, arrow }) => (
                <motion.div
                  key={from}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center gap-3 bg-white rounded-xl border border-border-light px-5 py-4 shadow-sm"
                >
                  <span className="text-sm font-bold text-text-primary min-w-[80px]">{from}</span>
                  <span className="text-text-muted text-sm font-semibold">{arrow}</span>
                  <span className="text-sm text-text-secondary">{to}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Detta är vad du får ── */}
      <section className="py-14 bg-white border-b border-border-light">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-6 text-center"
            >
              Detta är vad du får
            </motion.p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { title: 'Färre manuella uppgifter', desc: 'Rutinarbete som upprepas dagligen eller veckovis sköts automatiskt.' },
                { title: 'Snabbare svar till kunder', desc: 'Automatiska svar, bekräftelser och påminnelser — utan fördröjning.' },
                { title: 'Mindre administration', desc: 'Rapporter, uppdateringar och dokumentation genereras utan manuellt arbete.' },
              ].map(({ title, desc }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-surface-subtle rounded-xl border border-border-light p-5"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mb-3" />
                  <p className="text-[15px] font-bold text-text-primary mb-1">{title}</p>
                  <p className="text-[13px] text-text-secondary leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Main: accordion + sticky sidebar ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">

            {/* Accordion list (2/3 width) */}
            <div className="lg:col-span-2 space-y-3">
              {industries.map(item => (
                <AccordionCard
                  key={item.id}
                  item={item}
                  isOpen={openId === item.id}
                  onToggle={() => toggle(item.id)}
                />
              ))}
            </div>

            {/* Sticky sidebar (1/3 width) */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-5">

                {/* Trust points card */}
                <div className="bg-surface-subtle rounded-2xl border border-border-light p-6">
                  <p className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-5">Varför Aifinity</p>
                  <div className="space-y-4">
                    {[
                      { icon: '→', label: 'Snabb prototyp — se resultat innan du betalar', color: 'from-indigo-500 to-violet-600' },
                      { icon: '→', label: 'Integration mot system du redan har', color: 'from-emerald-400 to-teal-500' },
                      { icon: '→', label: 'Löpande support efter lansering', color: 'from-amber-400 to-orange-500' },
                    ].map(s => (
                      <div key={s.label} className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center shrink-0 mt-0.5`}>
                          <span className="text-white text-[10px] font-bold">{s.icon}</span>
                        </div>
                        <p className="text-[13px] text-text-secondary leading-snug">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA card */}
                <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 rounded-2xl p-6 text-white">
                  <p className="text-sm font-bold mb-2">Redo att automatisera?</p>
                  <p className="text-sm text-white/70 leading-relaxed mb-5">
                    Berätta om ett problem som tar tid varje vecka. Vi analyserar det gratis och säger om vi kan lösa det — vanligtvis på under 2 veckor.
                  </p>
                  <Link to="/contact">
                    <button className="w-full bg-white text-indigo-700 font-semibold text-sm py-3 rounded-xl hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2 group">
                      Beskriv ditt behov
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </Link>
                </div>

                {/* Trust note */}
                <p className="text-[12px] text-text-secondary text-center leading-relaxed px-2">
                  Svar inom 24 timmar. Vi säger om vi kan hjälpa — eller inte.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-4">
              Hittar du inte din bransch?
            </h2>
            <p className="text-text-secondary mb-8 max-w-md mx-auto">
              Vi bygger skräddarsydda automationslösningar för alla typer av B2B-företag. Kontakta oss och berätta om era processer.
            </p>
            <Link to="/contact">
              <button className="group btn-glow relative overflow-hidden bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2">
                Beskriv ditt behov
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </Link>
            <p className="text-xs text-text-muted mt-3">Svar inom 24 timmar · Du får ett konkret upplägg</p>
          </motion.div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default AutomationsPage;
